import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { OnboardingStatus } from '@app/pages/onboarding/onboarding.module';
import { environment } from '@environments/environment';
import { BillingSubscription } from '@interfaces/subscription';
import { User } from '@interfaces/user';
import { Observable, catchError, lastValueFrom, map, of, switchMap, tap } from 'rxjs';
import { ApiService } from './api.service';
import { BillingService } from './billing.service';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

  private userData = signal(<User | null>null);

  public companySubscription = signal<BillingSubscription | null>(null);
  public companySubscriptionObject = signal<BillingSubscription | null>(null);

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private billingService: BillingService,
  ) {}

  /**
   * Returns an observable that fetches the user profile from the server.
   */
  getUserProfile = (uuid: string): Observable<any> =>
    this.http.get(this.apiService.getUserDetailUrl(uuid), { headers: this.headers }).pipe(map((res) => res || {}));

  /**
   * Updates the user name with the provided data.
   */
  updateUserName = (userName: { first_name: string; last_name: string }, uuid: string): Observable<any> =>
    this.http.patch<any>(this.apiService.getUserDetailUrl(uuid), userName, { headers: this.headers });

  updateUserOnboardingStep = (onboardingStep: OnboardingStatus, uuid: string): Observable<any> => {
    const onboarding_data = { onboarding_status: onboardingStep };
    return this.http.patch<any>(this.apiService.getUserDetailUrl(uuid), onboarding_data, { headers: this.headers });
  };

  /**
   * Changes the user's password.
   */
  changePassword = (
    passwords: {
      new_password1: string;
      new_password2: string;
      current_password: string;
    },
    user: User,
  ): Observable<any> =>
    this.http.patch<any>(this.apiService.getPasswordChangeUrl(user.uuid), passwords, { headers: this.headers });

  /**
   *  Send password reset link to user email
   */
  sendPasswordResetLink = (resetEmailPayload: { email: string; operating_system: string }): Observable<any> => {
    return this.http.post(this.apiService.getPasswordResetUrl(), resetEmailPayload, { headers: this.headers });
  };

  /**
   * Send password reset confirmation
   */
  sendPasswordResetConfirmation = (resetPasswordPayload: {
    new_password: string;
    signature: string;
  }): Observable<any> => {
    const passwords = {
      new_password1: resetPasswordPayload.new_password,
      new_password2: resetPasswordPayload.new_password,
    };
    return this.http.post(this.apiService.getPasswordResetConfirmUrl(resetPasswordPayload.signature), passwords, {
      headers: this.headers,
    });
  };

  registerUser = (registerUserPayload: {
    email: string;
    first_name: string;
    last_name: string;
    other_data: {
      company: string;
      hear: string;
    };
  }) => {
    const newHeaders = this.headers.set('Authorization', `Basic ${environment.registerKey}`);
    return this.http.post(this.apiService.getRegistrationUrl(), registerUserPayload, { headers: newHeaders });
  };

  /**
   * Delete a user account
   * @param uuid - user uuid
   */
  deleteUserAccount = (uuid: string, isAnonymized: boolean = false): Observable<any> => {
    const endpoint = isAnonymized
      ? this.apiService.getDeleteUserUrl(uuid, true)
      : this.apiService.getUserDetailUrl(uuid);
    return this.http.delete(endpoint, { headers: this.headers });
  };

  /**
   * Returns an observable that retrieves all users from the API.
   * @returns An observable that retrieves all users from the API.
   */
  getTeamUsers = (uuid: string, paginationParam?: string): Observable<any> => {
    const url = paginationParam
      ? this.apiService.getTeamMembersUrl(uuid) + paginationParam
      : this.apiService.getTeamMembersUrl(uuid);
    return this.http.get(url);
  };

  /**
   * Returns an observable that add a new user to the team.
   * @param user - {email: string, first_name: string, last_name: string, role: string, company: string}
   * @returns An observable that add a new user to the team.
   */
  addTeamUser = (user: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    company: { uuid: string };
  }): Observable<any> => {
    return this.http.post(this.apiService.addTeamMemberUrl(), user);
  };

  /**
   * Returns the user data
   * * @returns {User | null} userData or null if it doesn't exist.
   */
  getUserData = (): User | null => this.userData() || null;

  /**
   * Updates a user in the database.
   * @param updatedUser - The updated user object.
   * @returns An observable of the HTTP response.
   */
  updateUser = (updatedUser: User, uuid: string): Observable<any> =>
    this.http.patch(this.apiService.getUserDetailUrl(uuid), updatedUser);

  setUserData(userData: User | null): void {
    this.userData.set(userData);
  }

  clearUserData(): void {
    this.userData.set(null);
    this.companySubscription.set(null);
  }

  getUserDevices = (uuid: string): Observable<any> => {
    return this.http.get(this.apiService.getUserDevicesUrl(uuid));
  };

  resendInviteEmail = (uuid: string): Observable<any> => {
    return this.http.get(this.apiService.resendInviteEmailUrl(uuid));
  };

  /** Check if user is on free or trial
   * @returns boolean
   */
  isUserOnFreeTrial(): boolean {
    const isTrialing = this.companySubscription()?.status === 'trialing';
    return isTrialing;
  }

  isTrialExpiredOrPaused(): boolean {
    const isPaused = this.companySubscription()?.status === 'paused';
    const isTrialExpired = this.companySubscription()?.status === 'trial_expired';
    return isTrialExpired || isPaused;
  }

  /**
   * Get user's profile data and company subscription data.
   * @param userUuid The user's UUID.
   */
  public async getUserProfileAndCompanySubscription(userUuid: string): Promise<void> {
    return lastValueFrom(
      this.getUserProfile(userUuid).pipe(
        switchMap((userData) => {
          this.setUserData(userData);
          const companyUuid = userData.company.uuid;

          return this.billingService.getCompanySubscription(companyUuid).pipe(
            tap((subscriptionResponse) => {
              this.companySubscription.set(subscriptionResponse.subscription);
              this.companySubscriptionObject.set(subscriptionResponse);
            }),
          );
        }),
        catchError((error) => {
          return of(undefined);
        }),
      ),
    );
  }

  /**
   * Get user's token hashes.
   * @param uuid The user's UUID.
   */
  getUserTokenHashes = (uuid: string, companyUuid: string): Observable<any> => {
    return this.http.get(this.apiService.getUserTokenHashesUrl(uuid, companyUuid));
  };

  /**
   *  Create user token hash.
   * @param {user: {uuid: string}, lifetime: number, name: string}
   * @returns {Observable<any>}
   */
  createUserTokenHash = (userUuid: string, companyUuid: string, lifetime: number, name: string): Observable<any> => {
    const tokenHash = {
      user: { uuid: userUuid },
      company: { uuid: companyUuid },
      lifetime,
      name,
    };
    return this.http.post(this.apiService.getTokenHashesUrl(), tokenHash);
  };

  /**
   *  Delete user token hash.
   * @param {string} uuid - The UUID of the token hash to delete.
   * @returns {Observable<any>} - An observable of the HTTP response.
   */
  deleteUserTokenHash = (uuid: string): Observable<any> => {
    return this.http.delete(this.apiService.getTokenHashesUrl() + uuid + '/');
  };

  getHasSubscription(): boolean {
    return (
      (this.companySubscription()?.status === 'active' ||
        (this.userData()?.num_days_until_trial_expiration_date ?? 0) >= 1) &&
      !this.isTrialExpiredOrPaused()
    );
  }

  verifyTurnstileToken = (token: string, remoteip: string = ''): Observable<any> => {
    const payload = {
      token: token,
      remoteip: remoteip, // Optionally, you can include the user's IP address here
    };
    return this.http.post(this.apiService.checkTurnstileTokenUrl(), payload);
  };

  getFeedjoltToken = (): Observable<any> => {
    return this.http.post(this.apiService.getFeedjoltTokenUrl(), {});
  };

  /**
   * Opens the feedback/support portal in a new tab. Shared by the sidebar's
   * "Provide feedback" button and the global header's help button.
   */
  openFeedbackPortal = (): void => {
    this.getFeedjoltToken().subscribe({
      next: (res) => {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      },
    });
  };
}
